import React, { useEffect, useState } from 'react';
import { Layout, Skeleton, Space, Grid, Tabs, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import fetchSessionsList from '../utils/session/fetchSessionsList';
import fetchPatientsList from '../utils/patient/fetchPatientsList';
import SessionList from '../components/session/SessionList';
import SessionEmptyState from '../components/session/SessionEmptyState';
import PatientList from '../components/session/PatientList';
import NewSessionButton from '../components/session/NewSessionButton';
import createCase from '../utils/case/createCase';
import useSessionPolling from '../hooks/useSessionPolling';

const { useBreakpoint } = Grid;
const { Content } = Layout;

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingPatients, setLoadingPatients] = useState(true);

  const [sessionPagination, setSessionPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [patientPagination, setPatientPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const screens = useBreakpoint();

  const initialHash = location.hash?.replace('#', '');
  const [activeTab, setActiveTab] = useState(
    initialHash === 'patients' ? 'patients' : 'sessions'
  );

  const handleSessionReady = (updatedSession) => {
    setSessions(prevSessions => 
      prevSessions.map(session => 
        session.getUUID() === updatedSession.getUUID() ? updatedSession : session
      )
    );
  };

  const activeSession = useSessionPolling(handleSessionReady);

  const fetchSessions = async (page) => {
    setLoadingSessions(true);
    const { sessions, pagination } = await fetchSessionsList(page);
    setSessions(sessions);
    setSessionPagination(pagination);
    setLoadingSessions(false);
  };

  const fetchPatients = async (page) => {
    setLoadingPatients(true);
    const { patients, pagination } = await fetchPatientsList(page);
    setPatients(patients);
    setPatientPagination(pagination);
    setLoadingPatients(false);
  };

  useEffect(() => {
    fetchSessions(sessionPagination.current);
    fetchPatients(patientPagination.current);
  }, [location.search]);

  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (hash === 'sessions' || hash === 'patients') {
      setActiveTab(hash);
    }
  }, [location.hash]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    navigate(`#${key}`, { replace: true });
  };

  const handleRecordClick = (file = null) => {
    if (file) {
      // Handle file upload
      handleUploadAudio(file);
    } else {
      // Handle live recording
      // setIsRecordingModalVisible(true);
    }
  };

  const handleUploadAudio = async (file) => {
    try {
      const response = await createCase({ audio: file });
      if (response && response.getUUID()) {
        navigate(`/cases/${response.getUUID()}?f=1`);
      }
    } catch (error) {
      message.error(t('session_recorder.errors.upload_failed'));
    }
  };

  if (loadingSessions || loadingPatients) {
    return (
      <Content className="page sessions-page">
        <Skeleton active paragraph={{ rows: 4 }} />
      </Content>
    );
  }

  const items = [
    {
      key: 'sessions',
      label: t('sessions.title'),
      children: (
        <SessionList
          sessions={sessions}
          pagination={sessionPagination}
          onPageChange={fetchSessions}
          activeSession={activeSession}
        />
      ),
    },
    {
      key: 'patients',
      label: t('sessions.patients_title'),
      children: (
        <PatientList
          patients={patients}
          pagination={patientPagination}
          onPageChange={fetchPatients}
        />
      ),
    },
  ];

  return (
    <Content className="page sessions-page">
      {sessions.length > 0 && (
        <div className="session-recorder-top">
          <NewSessionButton />
        </div>
      )}

      {sessions.length === 0 ? (
        <div data-testid="sessions-empty-state">
          <SessionEmptyState />
        </div>
      ) : (
        <div style={{ marginTop: 20 }}>
          <Tabs type="card" activeKey={activeTab} onChange={handleTabChange} items={items} />
        </div>
      )}
    </Content>
  );
}